// Turning a phone photo into something sendable from a yard.
//
// A screenshot taken on a modern phone is 1290x2796 and 3–8 MB. Sending that
// untouched over a field data connection is the difference between a report
// that arrives and one a worker gives up on halfway — and the server caps
// attachments at 3 MB, so the untouched original would simply be refused.
//
// Downscaling happens HERE, on the device, for that reason: the bytes that are
// too big must never leave the phone in the first place.
//
// JPEG rather than PNG: a UI screenshot re-encodes to roughly a tenth the size,
// and the softening is invisible at the thing this is for — seeing which button
// is missing or where the layout is cut off. Nobody is reading 8px text off it.

/** Longest edge we keep. Above this, nothing is gained for this purpose. */
export const MAX_EDGE = 1600;

/** Re-encode quality. Below ~0.7 UI text starts to smear. */
export const QUALITY = 0.85;

/**
 * Scale (w, h) to fit inside a max longest edge, preserving aspect ratio.
 *
 * Never scales UP — a small screenshot stays exactly as it is. Enlarging would
 * add bytes and no detail, which is the opposite of the point.
 *
 * @returns {{width: number, height: number}} whole pixels, never below 1
 */
export function fitWithin(width, height, maxEdge = MAX_EDGE) {
  const w = Math.max(0, Number(width) || 0);
  const h = Math.max(0, Number(height) || 0);
  if (!w || !h) return { width: 0, height: 0 };

  const longest = Math.max(w, h);
  if (longest <= maxEdge) return { width: Math.round(w), height: Math.round(h) };

  const scale = maxEdge / longest;
  return {
    // A 1600x1 strip must not round its short edge to 0 and produce a canvas
    // that throws.
    width: Math.max(1, Math.round(w * scale)),
    height: Math.max(1, Math.round(h * scale)),
  };
}

/**
 * Read an image File and return a downscaled JPEG data URL.
 *
 * Rejects with a message written for the person holding the phone, not for a
 * log: this surfaces directly in the report dialog.
 *
 * @param {File} file
 * @returns {Promise<{dataUrl: string, width: number, height: number, bytes: number}>}
 */
export function downscaleImage(file, maxEdge = MAX_EDGE, quality = QUALITY) {
  return new Promise((resolve, reject) => {
    if (!file || !String(file.type || '').startsWith('image/')) {
      reject(new Error('That does not look like an image.'));
      return;
    }

    const url = URL.createObjectURL(file);
    const img = new Image();

    // Revoked on every path — an object URL held open pins the whole decoded
    // image in memory, and a worker may attach several before sending.
    const done = (fn) => (arg) => { URL.revokeObjectURL(url); fn(arg); };
    const ok = done(resolve);
    const bad = done(reject);

    img.onerror = () => bad(new Error('That image could not be read.'));
    img.onload = () => {
      try {
        const { width, height } = fitWithin(img.naturalWidth, img.naturalHeight, maxEdge);
        if (!width || !height) {
          bad(new Error('That image could not be read.'));
          return;
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        // A screenshot with transparency would otherwise composite onto black
        // and read as a broken image.
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        // base64 is 4 chars per 3 bytes; good enough to show a size and to
        // catch something absurd before it is sent.
        const bytes = Math.round((dataUrl.length - dataUrl.indexOf(',') - 1) * 0.75);
        ok({ dataUrl, width, height, bytes });
      } catch (e) {
        bad(new Error('That image could not be processed on this device.'));
      }
    };

    img.src = url;
  });
}
